/**
 * This component will be rendered if page has static error
 *
 * @module views/components/StaticError
 * @memberof -Common
 */
import './StaticError.scss';

import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import GhostCards from '@ulta/core/components/GhostCards/GhostCards';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Icon from '@ulta/core/components/Icon/Icon';
import Spacer from '@ulta/core/components/Spacer/Spacer';
import Text from '@ulta/core/components/Text/Text';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';

import Link from '@ulta/modules/Link/Link';

import * as utils from './StaticError';

/**
 * Represents a StaticError component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns {JSX.Element} StaticError component
 */
export const StaticError = function( props ){
  const {
    error = defaultProps.error,
    errorImageUrl = defaultProps.errorImageUrl,
    errorText = defaultProps.errorText,
    errorSubText = defaultProps.errorSubText,
    ctaLink = defaultProps.ctaLink,
    isStaging
  } = props;

  const { closeOverlay } = useOverlay();

  useEffect( () => {
    // Always close overlays on error
    closeOverlay();
  }, [error] );

  if( isStaging ){
    return (
      <div className='StaticError'>
        <div className='StaticError__ultaLogo'>
          <Icon name='ultaSiteLogo'
            size='custom'
          />
        </div>
        <StagingWaitingRoom />
      </div>
    );
  }

  return (
    <GridContainer>
      <div className='StaticError'>
        <div className='StaticError__ultaLogo'>
          <Icon name='ultaSiteLogo'
            size='custom'
          />
        </div>
        <img src={ errorImageUrl }
          alt='error'
          className='StaticError__errorImage'
        />
        <div className='StaticError__errorText'>
          <Text textStyle='title-4'
            textAlign='center'
          >
            { errorText }
          </Text>
        </div>
        <div className='StaticError__errorSubText'>
          <Text textStyle='subtitle-2'
            textAlign='center'
          >
            { errorSubText }
          </Text>
        </div>
        <Link linkAction={ ctaLink }
          linkIcon='ArrowForward'
          linkStyle='title-5'
        />
      </div>
    </GridContainer>
  );
};

/**
 * Staging waiting room component that displays loading or stale content messages
 * during staging environment retries
 *
 * @method
 * @returns {JSX.Element} StagingWaitingRoom component
 */
export const StagingWaitingRoom = () => {
  const [isStaleContent, retryCount] = utils.useStagingRetries();

  return (
    <div className='StaticError__stagingWaitingRoom'>
      { !isStaleContent && (
        <>
          <Text htmlTag='h1'
            textStyle='title-2'
            textAlign='center'
          >
            Please Wait
          </Text>

          <Spacer value='01' />

          <Text htmlTag='h2'
            textStyle='title-3'
            textAlign='center'
          >
            Staging is under heavy load
          </Text>

          <Spacer value='03' />

          <Text fontWeight='bold'
            htmlTag='p'
            textStyle='body-2'
            textAlign='center'
          >
            Waiting for staging to be ready...
          </Text>

          <Spacer value='01' />

          <Text fontWeight='bold'
            htmlTag='p'
            textStyle='body-2'
            textAlign='center'
          >
            This page will auto-refresh { retryCount } times.
          </Text>

          <Spacer value='05' />
          <GhostCards />
        </>
      ) }

      { isStaleContent && (
        <>
          <Text htmlTag='h2'
            textStyle='title-3'
          >
            Staging is taking longer than expected
          </Text>

          <Spacer value='03' />

          <Text fontWeight='bold'
            htmlTag='p'
            textStyle='body-2'
            textAlign='center'
          >
            The content or preview may no longer exist
          </Text>

          <Spacer value='01' />

          <Text fontWeight='bold'
            htmlTag='p'
            textStyle='body-2'
            textAlign='center'
          >
            Please go back to the home page and add a previewDate
          </Text>

          <Spacer value='01' />

          <Text fontWeight='bold'
            htmlTag='p'
            textStyle='body-2'
            textAlign='center'
          >
            Redirecting to the home page in 5 seconds...
          </Text>
        </>
      ) }
    </div>
  );
};


/**
 * Custom hook to manage staging environment retry logic
 *
 * @method
 * @description Handles automatic page refresh retries when staging is under heavy load.
 * Tracks retry count via URL parameters and transitions to stale content state
 * after maximum retries are exhausted.
 *
 * @returns {[boolean, number]} Tuple containing:
 *  - isStaleContent: boolean indicating if content is stale
 *  - retryCount: number of maximum retries allowed
 */
export const useStagingRetries = () => {
  const { stagingWaitingRoomRetryInterval, stagingWaitingRoomRetryCount } = useAppConfigContext();
  const [isStaleContent, setIsStaleContent] = useState( false );

  // Add default values to prevent infinite retries
  const retryCount = stagingWaitingRoomRetryCount || 5;
  const retryInterval = stagingWaitingRoomRetryInterval || 8000;

  useEffect( () => {
    // Check if already marked as stale in URL (legacy support)
    if( window.location.href.includes( 'stale' ) ){
      setIsStaleContent( true );

      // Redirect to home after showing stale message for 5 seconds
      const redirectTimer = setTimeout( () => {
        const url = new URL( global.location.href );
        url.pathname = '/';
        url.search = '?previewDate=';
        global.location.href = url.href;
      }, 5000 );

      return () => clearTimeout( redirectTimer );
    }

    // Set up retry logic
    const retryTimer = setTimeout( () => {
      const url = new URL( global.location.href );
      const retries = parseInt( url.searchParams.get( 'stagingRetries' ), 10 ) || 0;

      if( retries >= retryCount ){
        // Max retries reached, show stale content then redirect
        setIsStaleContent( true );

        // Redirect after showing stale content for 5 seconds
        setTimeout( () => {
          url.pathname = '/';
          url.search = '?previewDate=';
          global.location.href = url.href;
        }, 5000 );
      }
      else {
        // Increment retry count and reload
        url.searchParams.set( 'stagingRetries', retries + 1 );
        global.location.href = url.href;
      }
    }, retryInterval );

    return () => clearTimeout( retryTimer );
  }, [retryCount, retryInterval] );

  return [isStaleContent, retryCount];
};

/**
 * Property type definitions
 *
 * @type {StaticError}
 * @property {string} errorImageUrl - error image url path
 * @property {string} errorText - error text
 * @property {string} errorSubText - button text
 * @property {string} ctaLink -  action for redirection to home page
 */
export const propTypes = {
  error: PropTypes.object,
  errorImageUrl: PropTypes.string,
  errorText: PropTypes.string,
  errorSubText: PropTypes.string,
  ctaLink: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 * @type {StaticError}
 * @property {string} errorImageUrl - error image url path
 * @property {string} errorText - error text
 * @property {string} errorSubText - button text
 * @property {string} ctaLink -  action for redirection to home page
 */
export const defaultProps = {
  error: {},
  errorImageUrl: '/ux/assets/static/media/ErrorPage.png',
  errorText: `We can't load this page right now. Please try again later.`,
  errorSubText: `For now, return to Ulta Beauty's home page and keep browsing.`,
  ctaLink: {
    label: 'Home',
    url: '/'
  }
};

StaticError.propTypes = propTypes;
StaticError.defaultProps = defaultProps;

export default StaticError;
